import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { formatarDataCivilFinanceiro } from "../helpers/formatar-data-financeiro";
import MDButton from "components/MDButton";
import FormField from "layouts/pages/account/components/FormField";
import type {
  BaixaTituloDialogState,
  BaixaTituloForm,
} from "../types/baixa-titulo.types";
import type { ContaFinanceiraFiltroApi } from "../types/extrato-financeiro.types";
import {
  formatarMoedaBRL,
  parseValorMonetarioInput,
} from "../helpers/financeiro-formatters";
import {
  CONTAS_FIN_ACCENT,
  fieldSx,
  modalContentSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "../../components/contas-financeiras.theme";

type OpcaoConta = { value: string; label: string; saldoAtual?: number };

const INPUT_HEIGHT = 44;
const fieldProps = {
  InputProps: { style: { height: INPUT_HEIGHT } },
  sx: fieldSx,
};

export type BaixaTituloDialogProps = {
  dialog: BaixaTituloDialogState;
  form: BaixaTituloForm;
  setForm: Dispatch<SetStateAction<BaixaTituloForm>>;
  contasFinanceiras: ContaFinanceiraFiltroApi[];
  contasLoading: boolean;
  contasError: string | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

function labelVencimento(iso: string | null | undefined): string {
  return formatarDataCivilFinanceiro(iso);
}

function ResumoMetrica({
  label,
  valor,
  destaque,
}: {
  label: string;
  valor: string;
  destaque?: boolean;
}) {
  return (
    <Box flex={1} minWidth={0}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        color={destaque ? CONTAS_FIN_ACCENT : "text.primary"}
        noWrap
      >
        {valor}
      </Typography>
    </Box>
  );
}

export default function BaixaTituloDialog({
  dialog,
  form,
  setForm,
  contasFinanceiras,
  contasLoading,
  contasError,
  loading,
  error,
  onClose,
  onConfirm,
}: BaixaTituloDialogProps) {
  const ehPagar = dialog.tipo === "PAGAR";
  const tipoLabel = dialog.tipo === "RECEBER" ? "Receber" : "Pagar";
  const tituloPrincipal =
    dialog.tituloDescricao?.trim() ||
    dialog.tituloDetalhe?.trim() ||
    "Título financeiro";

  const opcoesConta: OpcaoConta[] = contasFinanceiras.map((c) => {
    const saldoLabel =
      ehPagar && c.saldoAtual != null && Number.isFinite(c.saldoAtual)
        ? ` — saldo ${formatarMoedaBRL(c.saldoAtual)}`
        : "";
    return {
      value: c.id,
      label: c.tipo ? `${c.nome} (${c.tipo})${saldoLabel}` : `${c.nome}${saldoLabel}`,
      saldoAtual: c.saldoAtual,
    };
  });
  const contaSel =
    opcoesConta.find((o) => o.value === form.contaFinanceiraId) ?? null;

  const contaAtual = contasFinanceiras.find(
    (c) => c.id === form.contaFinanceiraId,
  );
  const valorBaixaNum =
    dialog.valorAberto != null && Number.isFinite(dialog.valorAberto)
      ? dialog.valorAberto
      : parseValorMonetarioInput(form.valorBaixa);
  const saldoConta =
    contaAtual?.saldoAtual != null && Number.isFinite(contaAtual.saldoAtual)
      ? contaAtual.saldoAtual
      : null;
  const saldoInsuficiente =
    ehPagar &&
    saldoConta != null &&
    Number.isFinite(valorBaixaNum) &&
    valorBaixaNum > 0 &&
    valorBaixaNum > saldoConta + 0.009;

  const contaBloqueada =
    Boolean(contasError) || contasFinanceiras.length === 0;

  const podeConfirmar =
    !loading &&
    !contaBloqueada &&
    !saldoInsuficiente &&
    Boolean(form.contaFinanceiraId?.trim()) &&
    Boolean(form.dataBaixa?.trim()) &&
    (dialog.valorAberto != null && Number.isFinite(dialog.valorAberto)
      ? dialog.valorAberto > 0
      : Boolean(form.valorBaixa?.trim()));

  const valorAbertoLabel =
    dialog.valorAberto != null && Number.isFinite(dialog.valorAberto)
      ? formatarMoedaBRL(dialog.valorAberto)
      : "—";

  const patch =
    (field: keyof BaixaTituloForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.value;
      setForm((prev) => ({ ...prev, [field]: v }));
    };

  return (
    <Dialog
      open={dialog.open}
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby="baixa-titulo-dialog-title"
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <PaymentsOutlinedIcon sx={{ fontSize: 40, opacity: 0.9, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="baixa-titulo-dialog-title"
              variant="overline"
              sx={{ opacity: 0.75, letterSpacing: 1 }}
            >
              Baixa manual
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} noWrap title={tituloPrincipal}>
              {tipoLabel} — {tituloPrincipal}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, display: "block", mt: 0.25 }}>
              Registra a saída ou entrada na conta financeira selecionada.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={loading}
            sx={{ color: "inherit" }}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={modalContentSx}>
        <Stack spacing={2}>
          {error ? (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {error}
            </Alert>
          ) : null}
          {contasError ? (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Não foi possível carregar contas financeiras: {contasError}.
            </Alert>
          ) : null}
          {saldoInsuficiente && saldoConta != null ? (
            <Alert severity="error" sx={{ py: 0.5 }}>
              Saldo insuficiente na conta selecionada. Disponível:{" "}
              {formatarMoedaBRL(saldoConta)}.
            </Alert>
          ) : null}

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "grey.200",
              bgcolor: "grey.50",
              borderLeft: `4px solid ${CONTAS_FIN_ACCENT}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {tituloPrincipal}
            </Typography>
            {dialog.tituloDetalhe ? (
              <Typography variant="body2" color="text.secondary">
                {dialog.tipo === "RECEBER" ? "Cliente" : "Fornecedor"}:{" "}
                {dialog.tituloDetalhe}
              </Typography>
            ) : null}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 1.5 }}
            >
              <ResumoMetrica label="Valor em aberto" valor={valorAbertoLabel} destaque />
              <ResumoMetrica
                label="Vencimento"
                valor={labelVencimento(dialog.dataVencimento)}
              />
            </Stack>
          </Box>

          <Alert severity="warning" sx={{ py: 0.5 }}>
            Confirma a baixa deste título? Será gerada uma movimentação financeira.
          </Alert>

          {ehPagar && saldoConta != null && !saldoInsuficiente ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Saldo disponível na conta: {formatarMoedaBRL(saldoConta)}.
            </Alert>
          ) : null}

          <Autocomplete<OpcaoConta, false, false, false>
            size="small"
            sx={{ width: "100%" }}
            options={opcoesConta}
            loading={contasLoading}
            disabled={loading || contaBloqueada}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            value={contaSel}
            onChange={(_, v) =>
              setForm((prev) => ({
                ...prev,
                contaFinanceiraId: v?.value ?? "",
              }))
            }
            noOptionsText="Nenhuma conta financeira disponível"
            renderInput={(params) => (
              <FormField
                {...params}
                label="Conta financeira"
                required
                placeholder="Selecione a conta de saída"
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  style: { height: INPUT_HEIGHT },
                }}
                sx={fieldSx}
              />
            )}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FormField
                label="Data da baixa"
                type="date"
                value={form.dataBaixa}
                onChange={patch("dataBaixa")}
                disabled={loading}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                {...fieldProps}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FormField
                label="Valor da baixa"
                value={
                  dialog.valorAberto != null && Number.isFinite(dialog.valorAberto)
                    ? formatarMoedaBRL(dialog.valorAberto)
                    : form.valorBaixa
                }
                disabled
                required
                fullWidth
                helperText="Baixa integral — valor fixo conforme o saldo em aberto."
                {...fieldProps}
              />
            </Box>
          </Stack>

          <FormField
            label="Observação (opcional)"
            value={form.observacao}
            onChange={patch("observacao")}
            disabled={loading}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            placeholder="Informações adicionais sobre a baixa"
            sx={fieldSx}
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: "1px solid",
          borderColor: "grey.200",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <MDButton
          color="dark"
          variant="outlined"
          size="small"
          disabled={loading}
          onClick={onClose}
        >
          Cancelar
        </MDButton>
        <MDButton
          color="dark"
          variant="gradient"
          size="small"
          disabled={!podeConfirmar}
          onClick={() => void onConfirm()}
          sx={saveButtonSx}
        >
          {loading ? "Processando…" : "Confirmar baixa"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
